import { ParticipantPosition } from '@core/dashboard/components/DashboardContent/interfaces';
import { IRoom } from '@interfaces/rooms';
import { SessionStorage } from '@constants/storage';
import {
  getSessionStorageValue,
  removeSessionStorageValue,
  setSessionStorageValue,
} from '@helpers/sessionStorage';

export interface StoredParticipantPosition {
  eventId: string;
  roomId: string;
  x: number;
  y: number;
  z: number;
  r: number;
  at: number;
}

type Coordinates = Pick<StoredParticipantPosition, 'x' | 'y' | 'z' | 'r'>;

export type SpawnResolution =
  | {
      restore: true;
      room: IRoom;
      position: ParticipantPosition;
      ageMs: number;
    }
  | {
      restore: false;
      room: IRoom | null;
      position: ParticipantPosition;
      why: string;
    };

const ORIGIN: ParticipantPosition = { timestamp: 0, x: 0, y: 0, z: 0, r: 0 };

const isPlacedSomewhere = ({ x, y, z, r }: Coordinates) =>
  [x, y, z, r].every(Number.isFinite) &&
  !(x === 0 && y === 0 && z === 0 && r === 0);

export const rememberParticipantPosition = (
  record: StoredParticipantPosition,
): boolean => {
  if (!record.eventId || !record.roomId || !isPlacedSomewhere(record)) {
    return false;
  }

  setSessionStorageValue(SessionStorage.ParticipantPosition, record);

  return true;
};

export const recallParticipantPosition =
  (): StoredParticipantPosition | null => {
    let stored: Partial<StoredParticipantPosition> | null;

    try {
      stored = getSessionStorageValue<Partial<StoredParticipantPosition>>(
        SessionStorage.ParticipantPosition,
      );
    } catch {
      return null;
    }

    if (
      !stored ||
      typeof stored.eventId !== 'string' ||
      typeof stored.roomId !== 'string' ||
      typeof stored.x !== 'number' ||
      typeof stored.y !== 'number' ||
      typeof stored.z !== 'number' ||
      typeof stored.r !== 'number'
    ) {
      return null;
    }

    return {
      eventId: stored.eventId,
      roomId: stored.roomId,
      x: stored.x,
      y: stored.y,
      z: stored.z,
      r: stored.r,
      at: typeof stored.at === 'number' ? stored.at : 0,
    };
  };

export const forgetParticipantPosition = () =>
  removeSessionStorageValue(SessionStorage.ParticipantPosition);

const fresh = (why: string, room: IRoom | null): SpawnResolution => ({
  restore: false,
  room,
  position: ORIGIN,
  why,
});

export const resolveSpawn = ({
  stored,
  eventId,
  rooms,
  fallbackRoom,
  now,
}: {
  stored: StoredParticipantPosition | null;
  eventId: string;
  rooms: IRoom[];
  fallbackRoom: IRoom | null;
  now: number;
}): SpawnResolution => {
  if (!stored) {
    return fresh('nothing remembered in this tab', fallbackRoom);
  }

  if (stored.eventId !== eventId) {
    return fresh(
      `remembered for another event ${stored.eventId}`,
      fallbackRoom,
    );
  }

  const room =
    rooms.find((candidate) => candidate.roomId === stored.roomId) ??
    (fallbackRoom?.roomId === stored.roomId ? fallbackRoom : null);

  if (!room) {
    return fresh(
      `remembered room ${stored.roomId} is not in this event`,
      fallbackRoom,
    );
  }

  if (!isPlacedSomewhere(stored)) {
    return fresh('remembered position is the world origin', fallbackRoom);
  }

  return {
    restore: true,
    room,
    position: {
      timestamp: stored.at,
      x: stored.x,
      y: stored.y,
      z: stored.z,
      r: stored.r,
    },
    ageMs: Math.max(0, now - stored.at),
  };
};

export const describeSpawn = (spawn: SpawnResolution): string => {
  if (!spawn.restore) {
    return `INIT spawn: fresh at the PlayerStart of ${
      spawn.room?.name ?? 'the default room'
    } — ${spawn.why}`;
  }

  const { x, y, z, r } = spawn.position;

  return `INIT spawn: restoring the last position in ${spawn.room.name} (${
    spawn.room.map?.ueIdentifier ?? spawn.room.roomId
  }) x=${Math.round(x)} y=${Math.round(y)} z=${Math.round(z)} r=${Math.round(
    r,
  )} — remembered ${Math.round(spawn.ageMs / 1000)}s ago`;
};
